import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { api, apiError, brl, pct } from "@/lib/api";
import { useCityTheme } from "@/context/CityThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = {
  name: "", state: "RJ", municipal_fee_pct: 3.5, admin_fee_pct: 2.5,
  ref_l1_pct: 3.5, ref_l2_pct: 2.5, service_fee_fixed: 0.99,
  prize_top1: 300, prize_top2: 150, prize_top3: 50,
  accent_color: "#1F3864", logo_url: "", active: true,
};

export default function AdminCities() {
  const { reloadCities, setCity } = useCityTheme();
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/cities").then(({ data }) => setCities(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;

  const save = async () => {
    const payload = {
      ...form,
      municipal_fee_pct: num(form.municipal_fee_pct), admin_fee_pct: num(form.admin_fee_pct),
      ref_l1_pct: num(form.ref_l1_pct), ref_l2_pct: num(form.ref_l2_pct),
      service_fee_fixed: num(form.service_fee_fixed),
      prize_top1: num(form.prize_top1), prize_top2: num(form.prize_top2), prize_top3: num(form.prize_top3),
      logo_url: form.logo_url || null,
    };
    try {
      if (editing) await api.put(`/cities/${editing}`, payload);
      else await api.post("/cities", payload);
      toast.success(editing ? "Cidade atualizada" : "Cidade cadastrada");
      setOpen(false); setForm(EMPTY); setEditing(null);
      load(); reloadCities();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const total = (c) => c.municipal_fee_pct + c.admin_fee_pct + c.ref_l1_pct + c.ref_l2_pct;

  return (
    <div data-testid="admin-cities">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Gestão de cidades (white-label)</h1>
          <p className="text-sm text-gray-500">
            Cada prefeitura recebe sua marca “Cidade + Time Food”, sua logo e suas alíquotas
          </p>
        </div>
        <Button data-testid="add-city-button" onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }}
          className="h-10 rounded-xl gf-gradient text-white font-bold gf-press">
          <Plus size={16} className="mr-1" /> Nova cidade
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-6 gf-stagger" data-testid="cities-list">
        {cities.map((c) => (
          <div key={c.id} className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={`city-card-${c.name}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-display text-lg font-extrabold" style={{ color: c.accent_color }}>
                  {c.brand_name || `${c.name} Time Food`}
                </p>
                <p className="text-xs text-gray-500">{c.name}/{c.state}</p>
              </div>
              <button data-testid={`edit-city-${c.name}`} onClick={() => { setForm({ ...EMPTY, ...c }); setEditing(c.id); setOpen(true); }}
                className="p-2 gf-press"><Pencil size={15} className="text-gray-500" /></button>
            </div>
            <div className="grid grid-cols-2 gap-y-1 mt-3 text-xs">
              <span className="text-gray-500">Prefeitura</span>
              <span className="text-right font-bold text-mage-navy">{pct(c.municipal_fee_pct)}</span>
              <span className="text-gray-500">Time Food</span>
              <span className="text-right font-bold text-gf-orange">{pct(c.admin_fee_pct)}</span>
              <span className="text-gray-500">Indicação nível 1</span>
              <span className="text-right font-bold">{pct(c.ref_l1_pct)}</span>
              <span className="text-gray-500">Indicação nível 2</span>
              <span className="text-right font-bold">{pct(c.ref_l2_pct)}</span>
              <span className="text-gray-500">Taxa de serviço</span>
              <span className="text-right font-bold">{brl(c.service_fee_fixed)}</span>
              <span className="text-gray-500">Prêmios do ranking</span>
              <span className="text-right font-bold text-mage-green">
                {brl(c.prize_top1 ?? 300)} · {brl(c.prize_top2 ?? 150)} · {brl(c.prize_top3 ?? 50)}
              </span>
              <span className="text-gray-500 font-semibold">Total por pedido</span>
              <span className="text-right font-extrabold">{pct(total(c))}</span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <span className="inline-block text-[10px] font-bold px-2 py-1 rounded-full text-white"
                style={{ background: c.active ? "#2E8B57" : "#9CA3AF" }}>
                {c.active ? "ATIVA" : "INATIVA"}
              </span>
              <button data-testid={`present-city-${c.name}`} onClick={() => { setCity(c.id); toast.success(`Modo apresentação: ${c.brand_name || c.name}`); }}
                className="text-[10px] font-bold px-2 py-1 rounded-full border border-gf-orange text-gf-orange gf-press">
                USAR NA APRESENTAÇÃO
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[460px] rounded-2xl max-h-[90vh] overflow-auto" data-testid="city-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar cidade" : "Nova cidade"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <F label="Nome da cidade" testId="city-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <F label="Estado (UF)" testId="city-state-input" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
            <F label="Taxa Prefeitura (%)" testId="city-municipal-fee-input" value={form.municipal_fee_pct} onChange={(v) => setForm({ ...form, municipal_fee_pct: v })} />
            <F label="Taxa administradora Time Food (%)" testId="city-admin-fee-input" value={form.admin_fee_pct} onChange={(v) => setForm({ ...form, admin_fee_pct: v })} />
            <F label="Indicação nível 1 (%)" testId="city-ref1-input" value={form.ref_l1_pct} onChange={(v) => setForm({ ...form, ref_l1_pct: v })} />
            <F label="Indicação nível 2 (%)" testId="city-ref2-input" value={form.ref_l2_pct} onChange={(v) => setForm({ ...form, ref_l2_pct: v })} />
            <F label="Taxa de serviço fixa (R$)" testId="city-service-fee-input" value={form.service_fee_fixed} onChange={(v) => setForm({ ...form, service_fee_fixed: v })} />
            <F label="Prêmio 1º lugar do ranking (R$)" testId="city-prize1-input" value={form.prize_top1} onChange={(v) => setForm({ ...form, prize_top1: v })} />
            <F label="Prêmio 2º lugar (R$)" testId="city-prize2-input" value={form.prize_top2} onChange={(v) => setForm({ ...form, prize_top2: v })} />
            <F label="Prêmio 3º lugar (R$)" testId="city-prize3-input" value={form.prize_top3} onChange={(v) => setForm({ ...form, prize_top3: v })} />
            <F label="Cor institucional (hex)" testId="city-color-input" value={form.accent_color} onChange={(v) => setForm({ ...form, accent_color: v })} />
            <F label="URL da logo da prefeitura" testId="city-logo-input" value={form.logo_url || ""} onChange={(v) => setForm({ ...form, logo_url: v })} />
            <div className="flex items-center justify-between">
              <Label className="text-xs text-gray-500">Cidade ativa</Label>
              <Switch data-testid="city-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            {form.name && (
              <p className="text-xs text-gray-500">
                Marca gerada: <b>{form.name} Time Food</b> · taxa total{" "}
                <b>{pct(num(form.municipal_fee_pct) + num(form.admin_fee_pct) + num(form.ref_l1_pct) + num(form.ref_l2_pct))}</b>
              </p>
            )}
            <Button data-testid="save-city-button" onClick={save} className="h-11 rounded-xl gf-gradient text-white font-bold">
              Salvar cidade
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-xs text-gray-500">{label}</Label>
    <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="h-11 rounded-xl mt-1" />
  </div>
);
